/**
 * TK-48 §2 — thẻ "Nguồn AC" khi đang chọn TỦ (hoặc đèn ⇒ tủ của đèn): 3 ngõ của tủ, mỗi ngõ một công tắc + "Trả về tự động".
 *   Cùng hai cửa lệnh của trang tủ / `BangTu` (`/api/cabinets/:id/control`, `/release`) — máy chủ gác quyền lần nữa.
 *   · MỌI lệnh hỏi xác nhận trước khi gửi (khác `BangTu`, nơi công tắc gửi ngay).
 *   · Khoá-không-ẩn: `<KhoaTheoTuyen co="canControl">`; tủ mất kết nối ⇒ công tắc khoá, trạng thái ngõ không khai.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RotateCcw } from 'lucide-react';
import { api, errorMessage, ApiError } from '../../lib/api';
import { qk, useCabinet } from '../../lib/queries';
import { useAuth } from '../../lib/auth';
import { KhoaTheoTuyen } from '../Khoa';
import { ConfirmDialog, Spinner, Switch, useToast } from '../ui';

type Hoi = { loai: 'ngo'; index: number; ten: string; on: boolean } | { loai: 'tra-ve' };

export function NguonAcTu({ cabinetId }: { cabinetId: string }) {
  const cab = useCabinet(cabinetId);
  const { coCoTuyen } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [hoi, setHoi] = useState<Hoi | null>(null);
  const lamMoi = () => {
    void qc.invalidateQueries({ queryKey: qk.cabinet(cabinetId) });
    void qc.invalidateQueries({ queryKey: qk.tree });
  };
  const control = useMutation({
    mutationFn: (v: { index: number; on: boolean }) => api.post<unknown>(`/api/cabinets/${cabinetId}/control`, { outputs: [{ index: v.index, on: v.on }] }),
    onSuccess: (_r, v) => { setHoi(null); toast.success(`Đã gửi lệnh ${v.on ? 'BẬT' : 'TẮT'} ngõ ra ${v.index}`, 'Đang chờ tủ xác nhận.'); lamMoi(); },
    onError: (e) => { setHoi(null); toast.error(errorMessage(e), e instanceof ApiError ? (e.hint ?? undefined) : undefined); },
  });
  const release = useMutation({
    mutationFn: () => api.post<unknown>(`/api/cabinets/${cabinetId}/release`),
    onSuccess: () => { setHoi(null); toast.success('Đã gửi lệnh trả quyền về chế độ tự động'); lamMoi(); },
    onError: (e) => { setHoi(null); toast.error('Không trả quyền được', errorMessage(e)); },
  });
  if (cab.isLoading || !cab.data) return <Spinner label="Đang tải tủ…" />;
  const c = cab.data;
  const qd = coCoTuyen('canControl', c.route?.id ?? null);
  const duoc = qd === 'cho-qua' && c.online;
  const ten = c.name;
  return (
    <div className="op-nguon-ac" data-o="nguon-ac-tu" data-tu={c.id}>
      <p className="op-nhan-lenh">{c.online ? 'Ngõ ra của tủ' : 'Tủ mất kết nối'}</p>
      <KhoaTheoTuyen qd={qd} co="canControl">
        {c.outputs.map((ng) => (
          <div key={ng.id} className="op-ngo" data-ngo={ng.index}>
            <span className="min-w-0 truncate">{ng.name || `Ngõ ra ${ng.index}`}</span>
            <Switch checked={c.online ? ng.isOn : false} disabled={!duoc || control.isPending}
              onChange={(v) => setHoi({ loai: 'ngo', index: ng.index, ten: ng.name || `Ngõ ra ${ng.index}`, on: v })}
              labelOn="Đang bật" labelOff={c.online ? 'Đã tắt' : '—'} />
          </div>
        ))}
        <button type="button" className="op-nut-phu op-tra-ve-tu-dong" disabled={!duoc || release.isPending} onClick={() => setHoi({ loai: 'tra-ve' })} data-o="tra-ve-tu-dong-tu">
          <RotateCcw className="h-4 w-4" aria-hidden="true" />Trả về tự động
        </button>
      </KhoaTheoTuyen>
      <ConfirmDialog open={hoi !== null} danger={false} loading={control.isPending || release.isPending}
        title={hoi?.loai === 'ngo' ? `${hoi.on ? 'Bật' : 'Tắt'} ${hoi.ten}?` : `Trả về tự động ${ten}?`}
        message={hoi?.loai === 'ngo' ? <>{ten} · {hoi.ten}</> : <>{ten} chạy lại theo lịch tại tủ</>}
        confirmText={hoi?.loai === 'ngo' ? (hoi.on ? 'Bật' : 'Tắt') : 'Trả về tự động'}
        onConfirm={() => { if (!hoi) return; if (hoi.loai === 'ngo') control.mutate({ index: hoi.index, on: hoi.on }); else release.mutate(); }}
        onCancel={() => setHoi(null)} />
    </div>
  );
}
